import { useEffect, useRef, useState } from 'react';
import { IVSViewer } from '@/utils/ivs/viewer';
import { Card } from '@/components/ui/card';
import { Wifi, WifiOff } from 'lucide-react';

interface WebRTCPlayerProps {
  streamId: string;
  viewerId: string;
}

export const WebRTCPlayer = ({ streamId, viewerId }: WebRTCPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const viewerRef = useRef<IVSViewer | null>(null);
  const [connectionState, setConnectionState] = useState<string>('Idle');

  useEffect(() => {
    const initializeViewer = async () => {
      if (!videoRef.current) return;

      try {
        const viewer = new IVSViewer(streamId, viewerId);
        viewerRef.current = viewer;

        await viewer.initialize(videoRef.current);
        setConnectionState('Playing');

        // Monitor player state
        const interval = setInterval(() => {
          if (viewerRef.current) {
            const state = viewerRef.current.getState();
            setConnectionState(state);
          }
        }, 1000);

        return () => clearInterval(interval);
      } catch (error) {
        console.error('Failed to initialize viewer:', error);
        setConnectionState('Error');
      }
    };

    initializeViewer();

    return () => {
      if (viewerRef.current) {
        viewerRef.current.disconnect();
      }
    };
  }, [streamId, viewerId]);

  return (
    <Card className="relative overflow-hidden bg-black">
      <div className="aspect-video relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="w-full h-full object-contain"
        />
        
        {/* Connection State Indicator */}
        <div className="absolute top-4 right-4 z-10">
          {connectionState === 'Playing' ? (
            <div className="flex items-center gap-2 bg-green-600/90 text-white px-3 py-1.5 rounded-full text-xs">
              <Wifi className="w-3 h-3" />
              Live
            </div>
          ) : (connectionState === 'Buffering' || connectionState === 'Ready') ? (
            <div className="flex items-center gap-2 bg-yellow-600/90 text-white px-3 py-1.5 rounded-full text-xs">
              <Wifi className="w-3 h-3 animate-pulse" />
              Loading...
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-red-600/90 text-white px-3 py-1.5 rounded-full text-xs">
              <WifiOff className="w-3 h-3" />
              Not Connected
            </div>
          )}
        </div>

        {/* Loading State */}
        {connectionState !== 'Playing' && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading stream...</p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
